import React from 'react';

import Stack from '@mui/material/Stack';
import ButtonBase from '@mui/material/ButtonBase';
import useMediaQuery from '@mui/material/useMediaQuery';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import {
    useChrono,
    useCurrentDebrief,
    useCurrentTracklog,
    useDebriefApp,
    useDebriefSegments,
    useMapControls,
    useMapSettings,
    useAnalytics,
} from 'ff/debrief/hooks';
import { Segment } from 'ff/debrief/models';
import SidebarPanel from 'ff/debrief/layouts/SidebarPanel';
import FlightInfo from 'ff/debrief/components/segments/FlightInfo';
import OverallScoreBar from 'ff/debrief/components/segments/OverallScoreBar';
import SegmentList from 'ff/debrief/components/segments/list/SegmentList';
import SectionHeader from 'ff/debrief/components/segments/SectionHeader';
import TopLevelScores from 'ff/debrief/components/segments/TopLevelScores';
import { RouteContext } from 'ff/debrief/router';
import { isSharedPage } from 'ff/debrief/utils';

const Segments = () => {
    const segmentListRef = React.useRef<HTMLDivElement>(null);
    const isConstrainedHeight = useMediaQuery('(max-height: 300px)');
    const isConstrainedWidth = useMediaQuery('(max-width: 1452px)');

    const isConstrained = isConstrainedHeight || isConstrainedWidth;

    const {
        isShareApp,
        selectedSegment,
        selectSegment,
        isDecluttering,
        toggleDecluttering,
    } = useDebriefApp();
    const { tracklog, viewport } = useCurrentTracklog();
    const { debrief, isScoreModeEnabled } = useCurrentDebrief();
    const { segmentTree } = useDebriefSegments();
    const { flyToPoints, controls } = useMapControls();
    const { resetMap } = useMapSettings();
    const { setOffset, resetChrono, pause } = useChrono();
    const { logSegmentClicked } = useAnalytics();
    const { returnLink, page } = React.useContext(RouteContext);

    React.useEffect(() => {
        resetChrono();
    }, []);

    const handleSegmentClick = React.useCallback(
        (segment: Segment) => {
            logSegmentClicked({
                segmentName: segment.label,
                segmentScore: segment?.score || null,
            });
            pause();
            setOffset(segment.startOffsetInSeconds);
            selectSegment(segment);
            flyToPoints(segmentTree.getSegmentById(segment.id).points);
        },
        [logSegmentClicked]
    );

    const handleBackButtonClick = () => {
        selectSegment(null);
        resetChrono();
        resetMap();
    };

    const handleResetView = () => {
        pause();
        resetChrono();
        selectSegment(undefined);

        if (isDecluttering) {
            toggleDecluttering();
        }

        if (viewport?.bounds) {
            controls.fitBounds(viewport.bounds);
        }

        segmentListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <SidebarPanel>
            <Stack
                py={2}
                px={0.5}
                height="100%"
                sx={{
                    overflowY: isConstrained ? 'auto' : 'visible',
                    ...(isConstrained && {
                        minHeight: 0,
                        flex: '1 1 auto',
                    }),
                }}
            >
                <FlightInfo
                    {...tracklog}
                    action={
                        !isShareApp && (
                            <ButtonBase
                                component={returnLink}
                                onClick={handleBackButtonClick}
                                sx={{ padding: 1 }}
                            >
                                <ArrowBackIosIcon />
                            </ButtonBase>
                        )
                    }
                    hideTailNumber={isSharedPage(page)}
                />

                {debrief?.score && isScoreModeEnabled && (
                    <>
                        <OverallScoreBar
                            score={debrief?.score}
                            sx={{ mt: 2, mb: 3, px: 2 }}
                        />

                        <TopLevelScores scores={debrief?.topLevelScores} />
                    </>
                )}

                <SectionHeader onResetView={handleResetView} />
                <SegmentList
                    ref={segmentListRef}
                    segments={debrief?.debriefSegments}
                    currentSegmentId={selectedSegment?.id}
                    onSegmentClick={handleSegmentClick}
                    sx={{
                        overflowY: isConstrained ? 'visible' : 'auto',
                        ...(isConstrained && {
                            flex: 'none',
                        }),
                    }}
                />
            </Stack>
        </SidebarPanel>
    );
};

export default Segments;
